import { ReactElement, ReactNode } from 'react'

import { T } from '@tribeplatform/react-components/i18n'
import { cn } from '@tribeplatform/react-components/utils/cn'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Popover } from '@tribeplatform/react-ui-kit/Popover'

import { usePopoverTour } from './PopoverTourContext.js'
import { POPOVER_TOUR_STEPS } from './popoverTourSteps.js'

interface PopoverTourStepProps {
  step: number
  title: ReactNode
  description: ReactNode
  wrapperClassName?: string
  children: ReactElement
}

export function PopoverTourStep({
  step,
  title,
  description,
  wrapperClassName,
  children,
}: PopoverTourStepProps) {
  const { currentStep, totalSteps, nextStep, skipTour } = usePopoverTour()
  const isActive = currentStep === step

  if (!isActive) {
    return children
  }

  const config = POPOVER_TOUR_STEPS[step]
  const isLastStep = step === totalSteps - 1

  return (
    <Popover
      open
      arrow
      onClose={() => {}}
      activator={children}
      placement={config.placement}
      wrapperClassName={wrapperClassName}
      autofocusTarget="none"
      padding="none"
    >
      <div className="bg-action-primary text-content-on-primary rounded-popover p-4 flex flex-col gap-3">
        <div className="flex gap-2 items-start">
          <div className="p-2 bg-action-primary-hovered rounded-base mt-1">
            <SvgIcon name="light-bulb" size="lg" className="shrink-0" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">{title}</span>
            <span className="text-sm opacity-80">{description}</span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <Button variant="primary" onClick={skipTour}>
            <T defaultMessage="Skip" id="Generics.Skip" />
          </Button>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors bg-content-on-primary',
                  i !== step && 'opacity-30',
                )}
              />
            ))}
          </div>
          <Button
            variant="secondary"
            // className="!bg-white !text-action-primary !border-transparent"
            onClick={nextStep}
          >
            {isLastStep ? (
              <T defaultMessage="Got it" id="Admin.AdminModeOnboarding.GotIt" />
            ) : (
              <T defaultMessage="Next" id="Generics.Next" />
            )}
          </Button>
        </div>
      </div>
    </Popover>
  )
}
